import pfpImage from "@/assets/pfp.png";
import { Avatar } from "@/components/ui";
import type { PropsWithChildren } from "react";

/**
 * One source of truth for chat bubble presentation.
 *
 * Onboarding and the section chats used to style their bubbles independently, which is why
 * AI replies were full-width in one surface and 70% in the other. Both now compose these
 * shells, so width, avatar, tail and surface tint stay identical everywhere — including the
 * onboarding summary's "View conversation" transcript, which renders through the section
 * chat's message list.
 */

/** 70% on tablet and up; a little wider on phones, where 70% minus the avatar is unreadable. */
const ROW_WIDTH = "w-[85%] sm:w-[70%]";

type TAiBubbleProps = PropsWithChildren<{
  /**
   * "progress" is a transient status item (e.g. summary generation) rather than something
   * the assistant said, so it takes a primary-tinted surface while keeping the bubble
   * silhouette.
   */
  kind?: "message" | "progress";
  /** Rendered under the bubble — timestamps, etc. */
  footer?: React.ReactNode;
}>;

export const AiBubble = ({ kind = "message", footer, children }: TAiBubbleProps) => {
  const isProgress = kind === "progress";

  return (
    <div className={`flex ${ROW_WIDTH} items-start gap-3`}>
      <div className="mt-1 flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-card shadow-sm">
        <img
          src="/fundy.svg"
          alt="Fundy"
          className="h-5 w-5 object-contain"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div
          data-testid={isProgress ? "ai-status-bubble" : "ai-message-bubble"}
          className={`min-w-0 rounded-2xl rounded-bl-[4px] border px-4 py-4 shadow-sm ${
            isProgress ? "border-primary/25 bg-primary/5 text-foreground" : "border-border bg-card text-card-foreground"
          }`}
        >
          {children}
        </div>
        {footer}
      </div>
    </div>
  );
};

type TUserBubbleProps = PropsWithChildren<{ footer?: React.ReactNode }>;

export const UserBubble = ({ footer, children }: TUserBubbleProps) => (
  <div className={`flex ${ROW_WIDTH} items-start gap-3`}>
    <div className="flex min-w-0 flex-1 flex-col">
      <div
        data-testid="user-message-bubble"
        className="min-w-0 rounded-2xl rounded-br-[4px] border border-primary/25 bg-primary/10 px-4 py-3 text-foreground shadow-sm"
      >
        {children}
      </div>
      {footer}
    </div>
    <div className="mt-1 flex-shrink-0">
      <Avatar
        src={pfpImage}
        alt="User avatar"
        size="default"
      />
    </div>
  </div>
);
